/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors } from '../../../shared/theme/colors';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import { styles } from '../ReportsScreen.styles';
import { ReportsDateRangePicker } from './ReportsDateRangePicker';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { OperationalFiltersCard } from '../../../shared/filters/OperationalFiltersCard'
import { OperationalFiltersPanelShell } from '../../../shared/filters/OperationalFiltersPanelShell';

type Props = {
  vm: ReportsScreenViewModel;
  disabled?: boolean;
  presetAccessibilityPrefix?: string;
};

/**
 * Panel de período compartido — solo selector de fecha + indicador de carga.
 * Para pestañas que no necesitan filtros de ítems (Resumen, Operación).
 * Ventas y Personal usan ReportsScopeFiltersPanel que incluye esta misma lógica
 * junto con filtros de ítems.
 */
const PERIOD_FILTER_COLLAPSED_KEY = 'reports_period_filter_collapsed';

export function ReportsPeriodFilterPanel({
  vm,
  disabled = false,
  presetAccessibilityPrefix = 'Período',
}: Props) {
  const busy = disabled || vm.loading;
  // Default colapsado: el período se resume en el hint; el usuario expande para cambiarlo.
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    let alive = true;
    void AsyncStorage.getItem(PERIOD_FILTER_COLLAPSED_KEY).then((raw) => {
      if (alive) setCollapsed(raw == null ? true : raw === '1');
    });
    return () => { alive = false; };
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    void AsyncStorage.setItem(PERIOD_FILTER_COLLAPSED_KEY, next ? '1' : '0');
  };

  return (
    <OperationalFiltersCard>
      <OperationalFiltersPanelShell
        title="Período"
        hintCompact={vm.dateRangeLabel}
        isMobile
        collapsible
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        showDividerBeforeBody
      >
        <ReportsDateRangePicker
          dateRangePreset={vm.dateRangePreset}
          setDateRangePreset={vm.setDateRangePreset}
          customRangeFrom={vm.customRangeFrom}
          setCustomRangeFrom={vm.setCustomRangeFrom}
          customRangeTo={vm.customRangeTo}
          setCustomRangeTo={vm.setCustomRangeTo}
          disabled={busy}
          presetAccessibilityPrefix={presetAccessibilityPrefix}
        />
        {busy ? (
          <View style={styles.reportsScopeFiltersBusyRow}>
            <ActivityIndicator size="small" color={ticketColors.ink2} />
            <Text style={styles.reportsScopeFiltersBusyText}>Actualizando datos del período…</Text>
          </View>
        ) : null}
      </OperationalFiltersPanelShell>
    </OperationalFiltersCard>
  );
}
